import React, { createContext, useContext, useReducer } from 'react';

import {
    clampAreaToMapSize,
    DEFAULT_AREA,
    DEFAULT_MAP_SIZE,
    EditorArea,
    SizeMeters,
    TABLE_SHAPE_DEFAULTS,
    TableShapeSizes,
} from './mapGeometry';

export interface EntrancePosition {
    x: number; // 0..1 normalized against the global rectangle
    y: number; // 0..1 normalized against the global rectangle
}

export type TableShape = 'round' | 'square' | 'rectangle';

export interface EditorTable {
    id: string;
    label: string;
    shape: TableShape;
    seats: number;
    qrCode: string | null;
    x: number; // 0..1 center within the global rectangle
    y: number; // 0..1 center within the global rectangle
    width: number; // metres; 0 = legacy/unset, falls back to the per-shape default
    height: number; // metres; 0 = legacy/unset, falls back to the per-shape default
    rotation: number; // degrees clockwise, 0..359, about the table's own center
    isActive: boolean;
}

export interface TableMapDraft {
    name: string;
    isActive: boolean;
    gridEnabled: boolean;
    entrance: EntrancePosition | null;
    size: SizeMeters;
    area: EditorArea;
    /** Size given to newly placed tables; changing one resizes every table of that shape. Draft-only, never persisted. */
    tableDefaults: TableShapeSizes;
    tables: EditorTable[];
}

type WizardAction =
    | { type: 'SET_NAME'; payload: string }
    | { type: 'SET_IS_ACTIVE'; payload: boolean }
    | { type: 'SET_GRID_ENABLED'; payload: boolean }
    | { type: 'SET_ENTRANCE'; payload: EntrancePosition | null }
    | { type: 'SET_SIZE'; payload: SizeMeters }
    | { type: 'SET_AREA'; payload: Partial<EditorArea> }
    | { type: 'SET_TABLE_DEFAULT_SIZE'; payload: { shape: TableShape; size: SizeMeters } }
    | { type: 'ADD_TABLE'; payload: Omit<EditorTable, 'qrCode'> }
    | { type: 'UPDATE_TABLE_POSITION'; payload: { id: string; x: number; y: number } }
    | { type: 'UPDATE_TABLE'; payload: { id: string } & Partial<Omit<EditorTable, 'id'>> }
    | { type: 'REMOVE_TABLE'; payload: { id: string } };

interface WizardContextValue {
    draft: TableMapDraft;
    dispatch: React.Dispatch<WizardAction>;
}

const defaultDraft: TableMapDraft = {
    name: '',
    isActive: true,
    gridEnabled: true,
    entrance: null,
    size: DEFAULT_MAP_SIZE,
    area: DEFAULT_AREA,
    tableDefaults: TABLE_SHAPE_DEFAULTS,
    tables: [],
};

/** Per-axis clamp to the map. `0` is the legacy/unset sentinel and `Math.min` leaves it at `0`. */
function fitToMapSize(size: SizeMeters, mapSize: SizeMeters): SizeMeters {
    return {
        width: Math.min(size.width, mapSize.width),
        height: Math.min(size.height, mapSize.height),
    };
}

/** The backend rejects a table dimension larger than the map's own. */
function clampTableDefaults(defaults: TableShapeSizes, mapSize: SizeMeters): TableShapeSizes {
    return {
        round: fitToMapSize(defaults.round, mapSize),
        square: fitToMapSize(defaults.square, mapSize),
        rectangle: fitToMapSize(defaults.rectangle, mapSize),
    };
}

function wizardReducer(state: TableMapDraft, action: WizardAction): TableMapDraft {
    switch (action.type) {
        case 'SET_NAME':
            return { ...state, name: action.payload };
        case 'SET_IS_ACTIVE':
            return { ...state, isActive: action.payload };
        case 'SET_GRID_ENABLED':
            return { ...state, gridEnabled: action.payload };
        case 'SET_ENTRANCE':
            return { ...state, entrance: action.payload };
        case 'SET_SIZE': {
            const size = action.payload;
            return {
                ...state,
                size,
                area: clampAreaToMapSize(state.area, size),
                tableDefaults: clampTableDefaults(state.tableDefaults, size),
                tables: state.tables.map((t) => ({ ...t, ...fitToMapSize(t, size) })),
            };
        }
        case 'SET_AREA': {
            const area = clampAreaToMapSize({ ...state.area, ...action.payload }, state.size);
            return { ...state, area };
        }
        case 'SET_TABLE_DEFAULT_SIZE': {
            const { shape, size } = action.payload;
            return {
                ...state,
                tableDefaults: { ...state.tableDefaults, [shape]: size },
                tables: state.tables.map((t) =>
                    t.shape === shape ? { ...t, width: size.width, height: size.height } : t
                ),
            };
        }
        case 'ADD_TABLE':
            return {
                ...state,
                tables: [...state.tables, { ...action.payload, qrCode: null, isActive: true }],
            };
        case 'UPDATE_TABLE_POSITION':
            return {
                ...state,
                tables: state.tables.map((t) =>
                    t.id === action.payload.id
                        ? { ...t, x: action.payload.x, y: action.payload.y }
                        : t
                ),
            };
        case 'UPDATE_TABLE':
            return {
                ...state,
                tables: state.tables.map((t) =>
                    t.id === action.payload.id ? { ...t, ...action.payload } : t
                ),
            };
        case 'REMOVE_TABLE':
            return {
                ...state,
                tables: state.tables.filter((t) => t.id !== action.payload.id),
            };
        default:
            return state;
    }
}

const TableMapWizardContext = createContext<WizardContextValue | undefined>(undefined);

interface TableMapWizardProviderProps {
    children: React.ReactNode;
    initialDraft?: TableMapDraft;
}

export function TableMapWizardProvider({ children, initialDraft }: TableMapWizardProviderProps) {
    const [draft, dispatch] = useReducer(wizardReducer, initialDraft ?? defaultDraft);
    return (
        <TableMapWizardContext.Provider value={{ draft, dispatch }}>
            {children}
        </TableMapWizardContext.Provider>
    );
}

export function useTableMapWizard(): WizardContextValue {
    const ctx = useContext(TableMapWizardContext);
    if (!ctx) {
        throw new Error('useTableMapWizard must be used inside TableMapWizardProvider');
    }
    return ctx;
}

export type { WizardAction };
export type { EditorArea, AreaShapeKind, SizeMeters } from './mapGeometry';
